import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

//Services
import { SentryService } from "app/shared/services/sentry.service";
import { UtilitesService } from "./utilites.service";

//Models
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Permissao, Usuario } from "app/usuarios/models/user.model";

export const LOGOUT_FLAG = 'usuario_deslogado';

@Injectable({ providedIn: "root" })
export class AuthService {
    constructor(
        private _http: HttpClient,
        public utilitesService: UtilitesService,
        private sentryService: SentryService
    ) {}

    post(data: any): Observable<any> {
        return this._http.post(this.utilitesService.urlPrefix + "login", JSON.stringify(data), {
            headers: this.getAuthHeaders(),
        });
    }

    // TODO: TEST THIS
    setUser(user: any) {
        window.localStorage.setItem("user", JSON.stringify(user));

        // Configurar usuário no Sentry quando fizer login
        if (user) {
            this.sentryService.setUser({
                id: user.id?.toString() || "unknown",
                email: user.email,
                username: user.nome || user.name,
                role: user.perfil?.nome || user.role,
            });

            // Adicionar breadcrumb de login
            this.sentryService.addBreadcrumb(`Usuário ${user.nome || user.email} logou no sistema`, "auth", {
                userId: user.id,
                perfil: user.perfil?.nome,
            });
        }
    }

    getUser() {
        return window.localStorage.getItem("user");
    }

    setToken(token: any) {
        window.localStorage.setItem("token", token);

        // PROSP-594 (F2): token novo = sessão nova. Semeia a marca de inatividade
        // AGORA para a sessão nova NÃO herdar a ociosidade da sessão anterior
        // (mesma chave do IdleTimeoutService: "last_activity_at"). Ponto único por
        // onde passam TODOS os finalizadores de login — token/2FA (validate-token),
        // SSO (auth-handler) e troca de perfil (mudar-perfil) —, então a inicialização
        // da atividade da sessão nova vale para todos sem tocar naqueles componentes.
        // Guarda-se APENAS um timestamp; nenhum token/credencial é armazenado para
        // identificar a sessão. Reload da MESMA sessão não passa por aqui (lê getToken),
        // logo não zera o relógio.
        if (token) {
            try {
                window.localStorage.setItem("last_activity_at", String(Date.now()));
            } catch {
                /* storage indisponível: o IdleTimeoutService cai no fallback em memória */
            }
        }
    }

    //return token
    getToken() {
        return window.localStorage.getItem("token");
    }

    setTempUser(user: any) {
        window.localStorage.setItem("temp_user", JSON.stringify(user));
    }

    setTempToken(token: any) {
        window.localStorage.setItem("temp_token", token);
    }

    getTempUser() {
        return window.localStorage.getItem("temp_user");
    }

    getTempToken() {
        return window.localStorage.getItem("temp_token");
    }

    public isAuth() {
        const user = this.getUser();
        if (user == null || user == undefined || user == "null" || user == "undefined") {
            return false;
        }

        return true;
    }
    
    redirectToMicrosoftSso(): void {
        const baseUrl = this.utilitesService.urlPrefix.endsWith('/') 
            ? this.utilitesService.urlPrefix 
            : `${this.utilitesService.urlPrefix}/`;
            
        window.location.href = `${baseUrl}auth/microsoft`;
    }

    esqueceuSenha(params: any): Observable<any> {
        const headers = new Headers();
        headers.append("Content-Type", "application/json; charset=utf-8");

        return this._http.post(this.utilitesService.urlPrefix + "password/sendlink", JSON.stringify(params), {
            headers: this.getAuthHeaders(),
        });
    }

    cadastrarUsuario(params: any): Observable<any> {
        const headers = new Headers();
        headers.append("Content-Type", "application/json; charset=utf-8");

        return this._http.post(this.utilitesService.urlPrefix + "customers/create", JSON.stringify(params), {
            headers: this.getAuthHeaders(),
        });
    }

    atualizarUsuario(params: any): Observable<any> {
        const headers = new Headers();
        const token = this.getToken();

        headers.append("Content-Type", "application/json");
        headers.append("Accept", "application/json");
        headers.append("Authorization", "Bearer " + token);

        return this._http.post(this.utilitesService.urlPrefix + "customers/edit", JSON.stringify(params), {
            headers: this.getAuthHeaders(),
        });
    }

    atualizarFoto(params: any): Observable<any> {
        const headers = new Headers();
        const token = this.getToken();

        headers.append("Content-Type", "application/json");
        headers.append("Accept", "application/json");
        headers.append("Authorization", "Bearer " + token);

        return this._http.put(this.utilitesService.urlPrefix + "customers/photo", JSON.stringify(params), {
            headers: this.getAuthHeaders(),
        });
    }

    logout() {
        // Adicionar breadcrumb de logout antes de limpar
        this.sentryService.addBreadcrumb("Usuário fez logout", "auth");

        // Limpar usuário do Sentry
        this.sentryService.clearUser();

        window.localStorage.removeItem("user");
        window.localStorage.removeItem("token");
        // PROSP-594 (F2): remove a marca de inatividade da sessão anterior, para o
        // próximo login (mesmo navegador, incl. troca de usuário) não herdar a
        // ociosidade acumulada. Mesma chave do IdleTimeoutService: "last_activity_at".
        window.localStorage.removeItem("last_activity_at");

        window.sessionStorage.setItem(LOGOUT_FLAG, "true");
    }

    isSuperAdmin() {
        const user = JSON.parse(this.getUser() ?? "{}");

        if (user.role_id == 2) {
            return true;
        }

        return false;
    }

    isAdmin() {
        const user = JSON.parse(this.getUser() ?? "{}");

        if (user.role_id == 1) {
            return true;
        }

        return false;
    }

    getRoleId() {
        const user = JSON.parse(this.getUser() ?? "{}");
        return user.role_id;
    }

    /**
     * Id do usuário autenticado, como veio do login.
     *
     * O tipo é união porque o payload não garante número — quem compara precisa normalizar.
     */
    getUserId(): number | string | null {
        const user = JSON.parse(this.getUser() ?? "{}");
        return user.id ?? null;
    }

    getAuthHeaders() {
        const token = this.getToken();
        
        let headers = new HttpHeaders({
            "Content-Type": "application/json; charset=utf-8",
            Accept: "application/json",
            "Access-Control-Allow-Origin": "*"
        });
        if (token) {
            headers = headers.set('Authorization', 'Bearer ' + token);
        }
        return headers;
    }

    isAuthenticated(): boolean {
        const token = this.getToken();
        const user = this.getUser();

        const hasToken = !!token && token !== 'null' && token !== 'undefined';
        const hasUser = !!user && user !== 'null' && user !== 'undefined';

        return hasToken && hasUser;
    }
    getPermissoes() {
        const user: Usuario = JSON.parse(this.getUser() ?? "{}");
        let permissoes: Permissao[] = [];

        if (user != null && user.role != null && user.role != undefined) {
            permissoes = user.role.permissoes;
        }

        return permissoes;
    }

    isPermissao(permissao: string[]) {
        const permissoes: Permissao[] = this.getPermissoes();
        let p = false;

        for (let i = 0; i < permissao.length; i++) {
            for (let k = 0; k < permissoes.length; k++) {
                if (permissao[i] == permissoes[k].nome) {
                    p = true;
                }
            }
        }

        return p;
    }

    getAllUsers(params: any): Observable<any> {
        const headers = new Headers();
        const token = this.getToken();

        headers.append("Content-Type", "application/json");
        headers.append("Accept", "application/json");
        headers.append("Authorization", "Bearer " + token);

        const paramHttp = this.utilitesService.formurlencoded(params);
        let env = "";
        if (paramHttp != null && paramHttp != "") {
            env = "?" + paramHttp;
        }

        return this._http.get(this.utilitesService.urlPrefix + "usuarios" + env, { headers: this.getAuthHeaders() });
    }

    possuiPermissao(permissao: string) {
        const role: any = this.getPermissoes();

        let p = false;
        if (role != null && role.permissoes != null && role.permissoes != undefined) {
            for (let i = 0; i < role.permissoes.length; i++) {
                const permis = role.permissoes[i].permissoes;
                for (let k = 0; k < permis.length; k++) {
                    if (permissao == permis[k].nome) {
                        p = true;
                    }
                }
            }
        }

        return p;
    }

    possuiPermissaoMenu(permissao: string) {
        const role: any = this.getPermissoes();
        let p = false;
        for (let i = 0; i < role.permissoes.length; i++) {
            const permis = role.permissoes[i].permissoes;
            for (let k = 0; k < permis.length; k++) {
                if (permissao != undefined && permissao.indexOf(permis[k].nome) > -1) {
                    p = true;
                }
            }
        }

        return p;
    }

    possuiPermissaoPorModulo(modulo_id: any) {
        const role: any = this.getPermissoes();
        let p = false;
        for (let i = 0; i < role.permissoes.length; i++) {
            const permis = role.permissoes[i].permissoes;
            for (let k = 0; k < permis.length; k++) {
                if (modulo_id == permis[k].modulo_id) {
                    p = true;
                }
            }
        }

        return p;
    }

    validateToken(params: any): Observable<any> {
        const token = this.getTempToken();

        const headers = new HttpHeaders({
            "Content-Type": "application/json; charset=utf-8",
            Accept: "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: "Bearer " + token,
        });

        return this._http.post(this.utilitesService.urlPrefix + "auth/token", JSON.stringify(params), {
            headers: headers,
        });
    }

    deviceTokenValidate(params: any): Observable<any> {
        return this._http.post(this.utilitesService.urlPrefix + "auth/token", JSON.stringify(params), {
            headers: this.getAuthHeaders(),
        });
    }
}
